import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, Proposal } from '../../models/types';
import { toast } from 'react-toastify';
import { ChevronLeft, Clock, Euro, MapPin, Eye, Send, Calendar, History, MessageSquare, Check, X } from 'lucide-react';
import ProposalForm from '../../components/Proposal/ProposalForm';
import { format } from 'date-fns';
import ChatModal from '../../components/Chat/ChatModal';
import Avatar from '../../components/UI/Avatar';

interface RequesterProfile {
  firstName: string;
  lastName: string;
  companyName: string;
  userPhoto?: string;
  companyLogo?: string;
}

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  
  const [project, setProject] = useState<Project | null>(null);
  const [requesterProfile, setRequesterProfile] = useState<RequesterProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [userProposal, setUserProposal] = useState<Proposal | null>(null);
  const [projectProposals, setProjectProposals] = useState<Proposal[]>([]);
  const [hasRequesterMessage, setHasRequesterMessage] = useState(false);
  const [selectedChat, setSelectedChat] = useState<{
    projectId: string;
    providerId: string;
    providerName: string;
  } | null>(null);

  // Major European countries
  const countries = [
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'BE', name: 'Belgium' }
  ];
  
  useEffect(() => {
    if (!id) {
      console.error('No project ID provided');
      setLoading(false);
      return;
    }

    if (!currentUser) {
      console.log('No current user, waiting...');
      return; // Don't set loading to false here, wait for auth
    }
    
    const fetchData = async () => {
      try {
        console.log('Starting to fetch project data for ID:', id);
        setLoading(true);
        
        // Fetch project
        const docRef = doc(db, 'projects', id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          console.error('Project not found');
          toast.error(t('error.projectNotFound'));
          setProject(null);
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        console.log('Project data fetched:', data);
        
        const projectData = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Project;
        
        setProject(projectData);

        // Fetch requester profile for thumbnails
        try {
          console.log('Fetching requester profile for user:', data.userId);
          const requesterProfileDoc = await getDoc(doc(db, 'requesterProfiles', data.userId));
          if (requesterProfileDoc.exists()) {
            console.log('Requester profile found');
            setRequesterProfile(requesterProfileDoc.data() as RequesterProfile);
          } else {
            console.log('No requester profile found');
          }
        } catch (error) {
          console.warn('Could not fetch requester profile:', error);
        }
        
        // Only increment view count if user is not the project owner
        if (data.userId !== currentUser.uid) {
          try {
            await updateDoc(docRef, {
              views: (data.views || 0) + 1
            });
            console.log('View count updated');
          } catch (error) {
            console.warn('Could not update view count:', error);
            // Don't show error to user as this is not critical
          }
        }

        // Fetch user's proposal if exists (provider only)
        if (userData?.role === 'provider') {
          try {
            console.log('Fetching user proposals for provider:', currentUser.uid);
            const proposalsRef = collection(db, 'proposals');
            const q = query(
              proposalsRef,
              where('projectId', '==', id),
              where('providerId', '==', currentUser.uid)
            );
            
            const proposalSnap = await getDocs(q);
            if (!proposalSnap.empty) {
              console.log('User proposals found:', proposalSnap.size);
              const proposals = proposalSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
                updates: doc.data().updates?.map((update: any) => ({
                  ...update,
                  createdAt: update.createdAt?.toDate() || new Date()
                })) || []
              }));
              
              const sortedProposals = proposals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
              setUserProposal(sortedProposals[0] as Proposal);

              // Check if there are messages from the requester (only for accepted proposals)
              if (sortedProposals[0].status === 'accepted') {
                try {
                  const chatId = `${id}_${currentUser.uid}`;
                  const messagesRef = collection(db, `projects/${id}/chats/${chatId}/messages`);
                  const messagesQuery = query(
                    messagesRef,
                    where('senderId', '==', data.userId)
                  );
                  
                  const messagesSnap = await getDocs(messagesQuery);
                  setHasRequesterMessage(!messagesSnap.empty);
                  console.log('Requester messages check completed');
                } catch (chatError) {
                  console.warn('Could not check for requester messages:', chatError);
                }
              }
            } else {
              console.log('No user proposals found');
            }
          } catch (error) {
            console.error('Error fetching user proposal:', error);
            // Continue without showing error as this is not critical
          }
        }

        // Fetch all proposals if user is project owner
        if (data.userId === currentUser.uid) {
          try {
            console.log('Fetching all proposals for project owner');
            const proposalsRef = collection(db, 'proposals');
            const q = query(
              proposalsRef,
              where('projectId', '==', id)
            );
            
            const proposalsSnap = await getDocs(q);
            const proposals = proposalsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              updates: doc.data().updates?.map((update: any) => ({
                ...update,
                createdAt: update.createdAt?.toDate() || new Date()
              })) || []
            })) as Proposal[];
            
            proposals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            setProjectProposals(proposals);
            console.log('Project proposals fetched:', proposals.length);
          } catch (error) {
            console.error('Error fetching project proposals:', error);
            toast.error(t('error.loading'));
          }
        }

        console.log('Data fetching completed successfully');
      } catch (error) {
        console.error('Error fetching project:', error);
        toast.error(t('error.loading'));
        setProject(null);
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, currentUser, userData?.role, t]);
  
  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return t('project.timeline1');
      case '2-3':
        return t('project.timeline2-3');
      case '3-6':
        return t('project.timeline3-6');
      case '6+':
        return t('project.timeline6+');
      case 'unknown':
        return t('project.timelineUnknown');
      default:
        return timeline;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  const getStatusLabel = (status: string) => {
    return t(`project.${status}`);
  };

  const getProposalStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'accepted':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleProposalAction = async (proposalId: string, action: 'accept' | 'reject') => {
    try {
      const proposalRef = doc(db, 'proposals', proposalId);
      await updateDoc(proposalRef, {
        status: action === 'accept' ? 'accepted' : 'rejected',
        updatedAt: new Date()
      });

      // Update local state
      setProjectProposals(prevProposals =>
        prevProposals.map(proposal =>
          proposal.id === proposalId
            ? { ...proposal, status: action === 'accept' ? 'accepted' : 'rejected' }
            : proposal
        )
      );

      toast.success(t(`success.proposal${action === 'accept' ? 'Accepted' : 'Rejected'}`));
    } catch (error) {
      console.error('Error updating proposal:', error);
      toast.error(t('error.updating'));
    }
  };

  const openChat = (projectId: string, providerId: string, providerName: string) => {
    setSelectedChat({ projectId, providerId, providerName });
  };

  const openProviderChat = () => {
    if (project && currentUser && userData) {
      setSelectedChat({
        projectId: project.id,
        providerId: currentUser.uid,
        providerName: userData.username
      });
    }
  };

  const closeChat = () => {
    setSelectedChat(null);
  };
  
  // Show loading spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  // Show error state if no project found
  if (!project) {
    return (
      <div className="container mx-auto container-padding section-padding">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-primary-600 transition-colors inline-flex items-center text-button"
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-1" />
          {t('nav.back')}
        </button>
        <div className="bg-white rounded-lg shadow-md card-padding text-center">
          <h1 className="heading-md mb-4">{t('error.projectNotFound')}</h1>
          <p className="text-body text-gray-600 mb-6">{t('error.projectNotFoundDesc')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto container-padding section-padding">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-6 text-gray-600 hover:text-primary-600 transition-colors inline-flex items-center text-button"
      >
        <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-1" />
        {t('nav.back')}
      </button>
      
      {/* Project Header */}
      <div className="bg-white rounded-lg shadow-md card-padding mb-6">
        {/* Project Owner Info */}
        {requesterProfile && (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">
              {t('language') === 'fr' ? 'Projet lancé par :' : 'Project launched by:'}
            </p>
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <Avatar
                src={requesterProfile.userPhoto}
                name={`${requesterProfile.firstName} ${requesterProfile.lastName}`}
                type="user"
                size="lg"
              />
              <div className="flex-grow">
                <h3 className="heading-xs text-gray-900">
                  {requesterProfile.firstName} {requesterProfile.lastName}
                </h3>
                <p className="text-body-sm text-gray-600">{requesterProfile.companyName}</p>
                <p className="text-caption">Project Owner</p>
              </div>
              {requesterProfile.companyLogo && (
                <Avatar
                  src={requesterProfile.companyLogo}
                  name={requesterProfile.companyName}
                  type="company"
                  size="md"
                />
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="heading-md">{project.name}</h1>
            {userProposal && (
              <div className="mt-2 flex items-center">
                <span className={`px-3 py-1 rounded-full text-label ${getProposalStatusColor(userProposal.status)} mr-2`}>
                  {t(`proposal.${userProposal.status}`)}
                </span>
                <span className="text-caption">
                  {t('project.lastUpdated')}: {format(userProposal.createdAt, 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-label ${getStatusColor(project.status)}`}>
            {getStatusLabel(project.status)}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="spacing-sm">
            <div className="flex items-center text-gray-600">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              <span className="text-body-sm">{t('project.timeline')}: {getTimelineLabel(project.timeline)}</span>
            </div>
            
            {project.budget && (
              <div className="flex items-center text-gray-600">
                <Euro className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                <span className="text-body-sm">{t('project.budget')}: €{project.budget.toLocaleString()}</span>
              </div>
            )}
            
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              <span className="text-body-sm">{t('profile.country')}: {countries.find(c => c.code === project.country)?.name || project.country}</span>
            </div>
            
            <div className="flex items-center text-gray-600">
              <Eye className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              <span className="text-body-sm">{project.views} {t('project.views')}</span>
            </div>

            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              <span className="text-body-sm">{t('project.created')}: {format(project.createdAt, 'MMM d, yyyy')}</span>
            </div>

            {/* Proposal History */}
            {userProposal && userProposal.updates && userProposal.updates.length > 0 && (
              <div className="mt-6">
                <h3 className="heading-xs mb-3 flex items-center">
                  <History className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  {t('proposal.history')}
                </h3>
                <div className="spacing-sm">
                  {[...userProposal.updates].reverse().map((update, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-caption">
                          {t('project.updatedOn')} {format(update.createdAt, 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>
                      <p className="text-body-sm text-gray-700 whitespace-pre-wrap">{update.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div>
            <h2 className="heading-xs mb-2">{t('project.description')}</h2>
            <p className="text-body-sm text-gray-600">{project.shortDescription}</p>
            
            {project.longDescription && (
              <div className="mt-4">
                <h3 className="text-label mb-2">{t('project.detailedDescription')}</h3>
                <p className="text-body-sm text-gray-600 whitespace-pre-wrap">{project.longDescription}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="spacing-sm">
          {/* Submit/Update Proposal Button */}
          {project.status === 'active' && userData?.role === 'provider' && project.userId !== currentUser?.uid && (
            <button
              onClick={() => setShowProposalForm(true)}
              className="w-full bg-primary-600 text-white py-2 sm:py-3 rounded-md text-button hover:bg-primary-700 transition-colors flex items-center justify-center"
            >
              <Send className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              {userProposal ? t('proposal.update') : t('proposal.submit')}
            </button>
          )}

          {/* Chat Button - Only for accepted proposals */}
          {userData?.role === 'provider' && userProposal?.status === 'accepted' && (
            <button
              onClick={openProviderChat}
              className={`w-full py-2 sm:py-3 rounded-md text-button transition-colors flex items-center justify-center ${
                hasRequesterMessage 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              {hasRequesterMessage ? t('chat.answerToRequester') : t('chat.chatWith') + ' ' + t('language') === 'fr' ? 'Demandeur' : 'Requester'}
            </button>
          )}
        </div>
      </div>

      {/* Proposals List (for project owner) */}
      {project.userId === currentUser?.uid && projectProposals.length > 0 && (
        <div className="bg-white rounded-lg shadow-md card-padding">
          <h2 className="heading-sm mb-6">{t('project.proposals')} ({projectProposals.length})</h2>
          <div className="spacing-lg">
            {projectProposals.map((proposal) => (
              <div key={proposal.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-3">
                    <Avatar
                      src="" // We don't have provider photo in proposal data
                      name={proposal.providerName}
                      type="user"
                      size="md"
                    />
                    <div>
                      <h3 className="heading-xs">{proposal.providerName}</h3>
                      <span className="text-caption">
                        {t('project.submittedOn')} {format(proposal.createdAt, 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-label ${getProposalStatusColor(proposal.status)}`}>
                    {t(`proposal.${proposal.status}`)}
                  </span>
                </div>

                <div className="prose max-w-none mb-4">
                  <p className="text-body-sm text-gray-700 whitespace-pre-wrap">{proposal.message}</p>
                </div>

                {proposal.updates && proposal.updates.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-label text-gray-700 mb-2">{t('proposal.updatesHistory')}</h4>
                    <div className="spacing-xs">
                      {proposal.updates.map((update, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded">
                          <p className="text-caption mb-1">
                            {t('project.updatedOn')} {format(update.createdAt, 'MMM d, yyyy HH:mm')}
                          </p>
                          <p className="text-body-sm">{update.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  {proposal.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleProposalAction(proposal.id, 'reject')}
                        className="px-4 py-2 border border-red-500 text-red-500 rounded hover:bg-red-50 transition-colors flex items-center text-button-sm"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('proposal.reject')}
                      </button>
                      <button
                        onClick={() => handleProposalAction(proposal.id, 'accept')}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center text-button-sm"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {t('proposal.accept')}
                      </button>
                    </>
                  )}
                  {proposal.status === 'accepted' && (
                    <button
                      onClick={() => openChat(project.id, proposal.providerId, proposal.providerName)}
                      className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors flex items-center text-button-sm"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t('chat.chat')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proposal Form Modal */}
      {showProposalForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 card-padding">
            <h2 className="heading-sm mb-4">
              {userProposal ? t('proposal.update') : t('proposal.submit')}
            </h2>
            <ProposalForm
              project={project}
              existingProposal={userProposal}
              onClose={() => setShowProposalForm(false)}
            />
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          projectId={selectedChat.projectId}
          chatId={`${selectedChat.projectId}_${selectedChat.providerId}`}
          otherUserId={project.userId}
          otherUserName="Project Requester"
          onClose={closeChat}
        />
      )}
    </div>
  );
};

export default ProjectDetail;